import { useState } from 'react';
import { Icon } from '@whinerp/ui';
import { useSession } from '../app/session.js';
import { useT } from '../app/i18n.js';

/**
 * Which providers are not real, said permanently (part 3).
 *
 * A sandbox deployment composes emails nobody receives, answers from a model
 * that is not a model, and estimates locations it never looked up. Each of
 * those says so where it appears - but somebody using the product for an hour
 * needs to know once, and keep knowing.
 *
 * So: a strip, not a toast. It cannot be dismissed, for the same reason the
 * support-visit banner cannot - the fact does not stop being true when
 * somebody clicks. It can be EXPANDED, because "three providers are in
 * sandbox" is what you need at a glance and "which three, and what to do" is
 * what you need once.
 *
 * Nothing is drawn when everything is live, which is the common case in a real
 * deployment and the whole reason this is safe to make prominent.
 */
export function SandboxBadge(): JSX.Element | null {
  const t = useT();
  const { session } = useSession();
  const [open, setOpen] = useState(false);

  const providers = session.providers ?? [];
  const pretend = providers.filter((entry) => entry.mode !== 'live');
  if (pretend.length === 0) return null;

  const missing = pretend.filter((entry) => entry.mode === 'not-configured');

  return (
    <div
      data-region="provider-mode"
      data-sandbox-count={String(pretend.length)}
      role="status"
      className="flex flex-col gap-1 border-b border-b-rule bg-warning-tint px-gutter py-1 text-dense text-ink"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Icon name="Info" size="inline" decorative />
        <span data-sandbox-summary>
          {/*
            * Not configured is not sandbox, and the strip says which. One is a
            * deployment working as it was set up; the other is missing a
            * setting, and only the second is somebody's to fix now.
            */}
          {missing.length > 0
            ? t('provider.someMissing', {
                values: { sandbox: String(pretend.length - missing.length), missing: String(missing.length) },
              })
            : t('provider.allSandbox', { values: { count: String(pretend.length) } })}
        </span>
        <button
          type="button"
          data-sandbox-toggle
          aria-expanded={open}
          className="ms-auto rounded-control px-2 py-0.5 text-meta underline"
          onClick={() => setOpen((value) => !value)}
        >
          {open ? t('provider.hide') : t('provider.show')}
        </button>
      </div>

      {open ? (
        <ul className="flex flex-col gap-0.5 pb-1">
          {pretend.map((entry) => (
            <li key={entry.key} data-sandbox-provider={entry.key} className="text-meta text-ink-600">
              <span className="font-medium">{entry.label}</span>
              {' — '}
              {entry.mode === 'not-configured' ? t('provider.notConfigured') : t('provider.sandbox')}
              {/* What to do about it, where there is something to do. */}
              {entry.nextStep ? <span className="text-ink-meta"> {entry.nextStep}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
